import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLazyGetFleetTrack1LinkQuery } from "../../api/network/adminApiServices";
import {
  useGetNotificationSummaryQuery,
  useLazyGetBuzzerAlertListQuery,
} from "../../api/network/notificationApiServices";
import { useLogoutMutation } from "../../api/network/userApiService";
import {
  TAppCommonState,
  setMobileHelpersData,
  setModalsData,
  setModalsDataBuzzerAlertsInProgress,
  setModalsDataBuzzerAlertsViewed,
  TMobileHelpers,
  TModalsState,
  TUserAuthType,
} from "../../api/store/commonSlice";
import {
  BuzzerAlertStatuses,
  BuzzerAlertWithUUID,
} from "../../api/types/Notifications";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { TLegacyUser } from "../../api/types/User";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { useAccountTimezone } from "../../hooks/timezone";
import { routeUrls } from "../../navigation/routeUrls";
import { ROLE_ACCESS_LEVELS } from "../../pages/admin-details/role/constants";
import { useChatContext } from "../../pages/chat/contexts/chat.context";
import HelpPopup from "../../pages/help/components/helpPopup";
import ModalBuzzerAlertList from "../../pages/notifications/modalBuzzerAlertList";
import ModalBuzzerAlertSingle from "../../pages/notifications/modalBuzzerAlertSingle";
import ModalNotificationList from "../../pages/notifications/modalNotificationsAlertsList";
import { logoutEverywhere } from "../../services/authLogoutService";
import { TMapState } from "../../types/map";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";
import { getBuzzerAlertUUID } from "../../utils/notification";
import { getUserIntials } from "../../utils/string";
import { useLoggedInUserData, useLoggedInUserPerms } from "../../utils/user";
import AppAvatar from "../avatar";
import AdminsDropdown from "./adminsDropdown";
import GlobalSearchBox from "./globalSearchBox";
import MenuItemLabel from "./menuItemLabel";
import SessionTimeoutTimer from "./sessionTimeoutTimer";

interface IconWithCounterProps {
  src: string;
  alt: string;
  counter?: number;
  title: string;
}

const IconWithCounter: React.FC<IconWithCounterProps> = ({
  src,
  alt,
  counter = null,
  title,
}) => (
  <li className="relative flex cursor-pointer p-3" title={title}>
    <img
      src={src}
      alt={alt}
      className={`block size-6 ms-2.5 lg:ms-0 max-w-6${alt === "chat-icon" ? " translate-y-[1px]" : alt === "warning-icon" ? " -translate-y-[1px]" : ""}`}
    />
    <span className="lg:hidden px-5 text-heading-gray666">{title}</span>
    {counter !== null && (
      <span className="absolute top-0 -right-1 bg-white rounded-full shadow-sm shadow-gray-300 size-5 text-[11px] flex items-center justify-center">
        {counter}
      </span>
    )}
  </li>
);

const extractTokenExpiry = (accessToken?: string): number | null => {
  if (!accessToken) {
    return null;
  }
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) {
      return null;
    }
    const normalizedPayload = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const parsed = JSON.parse(window.atob(normalizedPayload));
    const exp = Number(parsed?.exp);
    return Number.isFinite(exp) && exp > 0 ? exp : null;
  } catch {
    return null;
  }
};

const DashboardHeader = () => {
  const { t } = useTranslation();
  const { t: tTop } = useTranslation("translation", {
    keyPrefix: "dashboard.top",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getLocalStorageItem } = useLocalStorage();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const accessToken = useSelector(
    (state: { commonReducer: TAppCommonState }) =>
      state.commonReducer.accessToken
  );
  const userAuthType = useSelector(
    (state: { commonReducer: { userAuthType: TUserAuthType } }) =>
      state.commonReducer.userAuthType
  );
  const loggedInUser: TLegacyUser =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const sessionExpiryFromToken = useMemo(
    () => extractTokenExpiry(accessToken),
    [accessToken]
  );
  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const mobileHelpers = useSelector(
    (state: { commonReducer: { mobileHelpers: TMobileHelpers } }) =>
      state.commonReducer.mobileHelpers
  );

  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );

  // retrieve account timezone
  const {
    userTzIsApplicable,
    userTzForDisplay,
    accountTzForDisplay,
    tzForDateFns,
    tzForDisplay,
  } = useAccountTimezone();

  // access control
  const { loggedInUserIsSysAdmin } = useLoggedInUserPerms();
  const { getUserACLAccessLevel } = useACL();

  const { pathname } = useLocation();
  const adminsMenuActive =
    pathname.indexOf(routeUrls.dashboardChildren.admins) > -1;

  const [logoutAPITrigger, { isLoading }] = useLogoutMutation();

  const handleLogout = () => {
    logoutAPITrigger().then(() => {
      logoutEverywhere({ redirect: false });
      dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
      navigate("/");
      window.location.reload();
    });
  };

  const [showProfileDropdown, setShowProfileDropdown] = React.useState(false);
  const handleClickProfileIcon = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const handleNavigateTo = (page: "account" | "change_password") => {
    hideMobileMenu();
    setShowProfileDropdown(false);
    switch (page) {
      case "account":
        navigate(routeUrls.dashboardChildren.account);
        break;
      case "change_password":
        navigate(routeUrls.changePassword);
        break;
    }
  };

  const thisOrgAllowsToggleFT = useLoggedInUserData("toggleFT");

  const [getFleetTrack1LinkAPITrigger, { isLoading: isLoadingFleetTrackLink }] =
    useLazyGetFleetTrack1LinkQuery();

  const navigateToFT1_0App = () => {
    hideMobileMenu();
    getFleetTrack1LinkAPITrigger()
      .unwrap()
      .then((res) => {
        if (res?.is_redirection_supported) {
          window.location.href = res?.redirect_url;
        } else {
          toast.error(tTop("redirect_ft_1_0_not_allowed"));
        }
      })
      .catch((err) => {
        if (APP_CONFIG.DEBUG.ALL) console.log(err);
      })
      .finally(() => {
        setShowProfileDropdown(false);
      });
  };

  // poll for notification summary
  const {
    data: dataNotificationSummary,
    isFetching: isFetchingNotificationSummary,
  } = useGetNotificationSummaryQuery(
    { polling: true },
    {
      pollingInterval: APP_CONFIG.API_POLLING_INTERVALS.NOTIFICATION_SUMMARY,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    }
  );

  // buzzer alerts
  const [
    getBuzzerAlertListAPITrigger,
    { isLoading: isLoadingBuzzerAlertList },
  ] = useLazyGetBuzzerAlertListQuery();

  // attempt to identify ignored buzzer alerts and remove them from store every 10 seconds
  // this method was found inefficient and has been disabled, we are now using a different approach
  // ----------------------------------------------------------------------------------------------
  // const [pseudoState, setPseudoState] = React.useState<TViewedBuzzerAlert[]>([]);
  // useEffect(() => {
  //   console.log('STATE CHANGE', modalsState?.buzzerAlertsViewed)
  //   setPseudoState(modalsState?.buzzerAlertsViewed)
  // }, [modalsState?.buzzerAlertsViewed])
  // const { getUnignoredBuzzerAlerts } = useBuzzerAlertActions();
  // const updateBuzzerAlerts = useCallback(() => {
  //   if (APP_CONFIG.DEBUG.BUZZER) console.error('CHECK (10s)', pseudoState.length);

  //   // clear ignored buzzer alerts from viewed list
  //   const unignoredBuzzerAlerts =
  //     modalsState?.buzzerAlertsViewed
  //       .filter((alert) => {
  //         const currentTime = new Date().getTime();
  //         const viewedAt = alert.viewedAt;
  //         const timeDiff = currentTime - viewedAt;
  //         const minutes = Math.floor(timeDiff / 1000 / 60);
  //         if (APP_CONFIG.DEBUG.BUZZER) console.log('Comparing calculated min:', minutes, 'with config min:', APP_CONFIG.BUZZER_ALERTS.IGNORED_MINUTES)
  //         return minutes < APP_CONFIG.BUZZER_ALERTS.IGNORED_MINUTES;
  //       });
  //   dispatch(setModalsData({
  //     ...modalsState,
  //     buzzerAlertsViewed: unignoredBuzzerAlerts,
  //   }));
  //   if (APP_CONFIG.DEBUG.BUZZER) console.log('unignored buzzer alerts', unignoredBuzzerAlerts);
  //   if (APP_CONFIG.DEBUG.BUZZER) console.log('ignored buzzer alerts should clear by now');
  // }, [pseudoState]);
  // // check and remove ignored buzzer alerts from store
  // useEffect(() => {
  //   const interval = setInterval(updateBuzzerAlerts, APP_CONFIG.BUZZER_ALERTS.IGNORED_BUZZER_ALERTS_CHECK);
  //   return () => clearInterval(interval);
  // }, []);

  // clear viewed buzzer alerts from store
  useEffect(() => {
    const interval = setInterval(
      () => {
        dispatch(setModalsDataBuzzerAlertsViewed([]));
      },
      APP_CONFIG.BUZZER_ALERTS.IGNORED_PENDING_MINUTES * 60 * 1000
    );
    return () => clearInterval(interval);
  }, []);

  // clear in-progress buzzer alerts from store
  useEffect(() => {
    const interval = setInterval(
      () => {
        dispatch(setModalsDataBuzzerAlertsInProgress([]));
      },
      APP_CONFIG.BUZZER_ALERTS.IGNORED_IN_PROGRESS_MINUTES * 60 * 1000
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isFetchingNotificationSummary) {
    } else if (!isFetchingNotificationSummary && !!dataNotificationSummary) {
      // fetch buzzer alerts
      if (!!dataNotificationSummary?.is_buzzer_alert) {
        if (APP_CONFIG.DEBUG.BUZZER) console.error("FETCH AT", new Date());
        getBuzzerAlertListAPITrigger({
          page: 1,
          page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
        })
          .unwrap()
          .then((res) => {
            const buzzerAlerts = res.results.map(
              (item) =>
                ({
                  ...item,
                  uuid: getBuzzerAlertUUID(item),
                }) as BuzzerAlertWithUUID
            );
            if (APP_CONFIG.DEBUG.BUZZER)
              console.log("all buzzer alerts", buzzerAlerts);

            const unreadBuzzerAlerts = buzzerAlerts
              .filter((item) => item.status === BuzzerAlertStatuses.PENDING) // only pending alerts are considered unread
              .filter(
                (item) =>
                  !modalsState.buzzerAlertsViewed
                    .map((alert) => alert.uuid)
                    .includes(item.uuid)
              ); // filter out viewed alerts
            if (APP_CONFIG.DEBUG.BUZZER)
              console.log("unread buzzer alerts", unreadBuzzerAlerts.length);

            const inProgressBuzzerAlerts = buzzerAlerts
              .filter((item) => item.status === BuzzerAlertStatuses.INPROGRESS) // only in-progress alerts are considered
              .filter(
                (item) =>
                  !modalsState.buzzerAlertsInProgress
                    .map((alert) => alert.uuid)
                    .includes(item.uuid)
              ); // filter out alerts marked in-progress
            if (APP_CONFIG.DEBUG.BUZZER)
              console.log(
                "in-progress buzzer alerts",
                inProgressBuzzerAlerts.length
              );

            const totalUnreadBuzzerAlertsCount =
              unreadBuzzerAlerts.length + inProgressBuzzerAlerts.length;
            if (APP_CONFIG.DEBUG.BUZZER)
              console.log(
                "totalUnreadBuzzerAlertsCount",
                totalUnreadBuzzerAlertsCount
              );

            dispatch(
              setModalsData({
                ...modalsState,
                buzzerAlerts,
                showBuzzerAlertList: totalUnreadBuzzerAlertsCount > 1,
                showBuzzerAlertSingle: totalUnreadBuzzerAlertsCount === 1,
              })
            );
          })
          .catch((err) => {})
          .finally(() => {});
      }
    }
  }, [isFetchingNotificationSummary, dataNotificationSummary]);

  const avatarInitials = useMemo(
    () =>
      getUserIntials(
        [
          loggedInUser?.displayname,
          loggedInUser?.contactname,
          loggedInUser?.contactemail,
          loggedInUser?.accountid,
        ].find((item) => !!item) ?? "-"
      ),
    [loggedInUserData, loggedInUser]
  );

  // get unread count from chat presence
  const { presence, dataSMSUnreadCount } = useChatContext();
  const chatUnreadCount = Object.keys(presence)
    .filter((key) => presence[key]?.unreadMessageCount > 0)
    .reduce((acc, key) => acc + presence[key]?.unreadMessageCount, 0);
  const smsUnreadCount = dataSMSUnreadCount?.unread_count ?? 0;
  const allMessagingUnreadCount = chatUnreadCount + smsUnreadCount;

  const hideMobileMenu = () => {
    dispatch(
      setMobileHelpersData({
        ...mobileHelpers,
        showRightMenuDrawer: false,
      })
    );
    dispatch(
      setModalsData({
        ...modalsState,
        showNotifications: false,
        showAdminsDropdown: false,
      })
    );
  };
  const [openHelpPopup, setOpenHelpPopup] = useState(false);

  const handleHelpIconClick = () => {
    if (pathname.startsWith(routeUrls.dashboardChildren.help)) {
      navigate(routeUrls.dashboardChildren.help);
      return;
    }
    setOpenHelpPopup(true);
    hideMobileMenu();
  };

  return (
    <>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <div className="absolute bg-white/90 top-0 left-1/2 -translate-x-1/4 z-below-popup-overlay text-[8px] max-h-32 overflow-auto">
          <pre>
            Redux store user:{" "}
            {`${loggedInUserData?.user?.userid} [${userAuthType}]`}
          </pre>
          <pre
            onClick={() => {
              dispatch(
                setModalsData({
                  ...modalsState,
                  buzzerAlertsViewed: modalsState.buzzerAlertsViewed.slice(1),
                })
              );
            }}
            className="pointer-events-auto hover:bg-accent-blue-bright/20"
          >
            Buzzer Alerts Viewed ({modalsState?.buzzerAlertsViewed?.length}):{" "}
            {JSON.stringify(modalsState.buzzerAlertsViewed)}
          </pre>
          <pre
            onClick={() => {
              dispatch(
                setModalsData({
                  ...modalsState,
                  buzzerAlertsInProgress:
                    modalsState.buzzerAlertsInProgress.slice(1),
                })
              );
            }}
            className="pointer-events-auto hover:bg-accent-blue-bright/20"
          >
            Buzzer Alerts In Progress (
            {modalsState?.buzzerAlertsInProgress?.length}):{" "}
            {JSON.stringify(modalsState.buzzerAlertsInProgress)}
          </pre>
          <pre>App Timezone (redux): {tzForDisplay ?? "-"}</pre>
          <pre>App Timezone date-fns (redux): {tzForDateFns ?? "-"}</pre>
          <pre>
            Unread: {chatUnreadCount} (chat), {smsUnreadCount} (sms)
          </pre>
          <pre>
            mapScriptLoaded:{" "}
            <span
              className={
                mapState?.mapScriptLoaded !== true
                  ? "text-field-error-dark"
                  : ""
              }
            >
              {mapState?.mapScriptLoaded ? "true" : "false"}
            </span>{" "}
            (typeof: {typeof mapState?.mapScriptLoaded})
          </pre>
          <pre>mapState:</pre>
          <pre>{JSON.stringify(mapState, null, 2)}</pre>
        </div>
      )}
      <div
        className={`fixed top-0 left-0 w-full ${userIsImpersonated ? "h-[calc(100vh-40px)]" : "h-dvh"} z-overlay bg-modal-overlay${showProfileDropdown ? "" : " hidden"}`}
        onClick={handleClickProfileIcon}
      ></div>
      <div
        className={
          `${APP_CONFIG.DES.DASH.P_HORIZ} py-2 lg:py-4 flex gap-3 lg:grid lg:grid-cols-12 lg:gap-0 ` +
          `bg-white fixed ${userIsImpersonated ? "top-10" : "top-0"} left-0 w-full z-below-below-overlay border-b border-x-border-gray ` +
          `lg:bg-transparent lg:static lg:top-auto lg:left-auto lg:w-auto lg:z-auto lg:border-b-0 `
        }
      >
        {/* Mobile Hamberger Icon */}
        <div className="lg:hidden flex items-center">
          <button
            type="button"
            className="w-10 h-10 rounded-md bg-accent-blue-paleO66 flex items-center justify-center group focus:bg-accent-blue-dark outline outline-1 outline-accent-blue-dark/50"
            onClick={() => {
              dispatch(
                setMobileHelpersData({
                  ...mobileHelpers,
                  showLeftMenuDrawer: !mobileHelpers.showLeftMenuDrawer,
                })
              );
            }}
          >
            <img
              src={svgIcons.HamburgerIcon}
              alt="hamberger-icon"
              className="block size-6 brightness-[0.7] group-focus:brightness-0 group-focus:invert"
            />
          </button>
        </div>

        {/* Global Search */}
        <div className="flex-grow lg:col-span-4 flex items-center">
          <GlobalSearchBox />
        </div>

        {/* Account Gear Icon */}
        <div className="lg:hidden flex items-center">
          <button
            type="button"
            className="w-10 h-10 rounded-md bg-accent-blue-paleO66 flex items-center justify-center group focus:bg-accent-blue-dark outline outline-1 outline-accent-blue-dark/50"
            onClick={() => {
              dispatch(
                setMobileHelpersData({
                  ...mobileHelpers,
                  showRightMenuDrawer: !mobileHelpers.showRightMenuDrawer,
                })
              );
            }}
          >
            <img
              src={svgIcons.AdminGearIcon}
              alt="hamberger-icon"
              className="block size-6 brightness-[0.7] group-focus:brightness-0 group-focus:invert"
            />
          </button>
        </div>

        {/* Admins Dropdown, Help, Chat, Warning, Notifications */}
        {mobileHelpers.showRightMenuDrawer && (
          <div
            className="fixed top-0 right-0 bottom-0 left-0 bg-black/50 backdrop-blur-[4px] z-below-overlay lg:hidden"
            onClick={hideMobileMenu}
          />
        )}
        <div
          className={
            `fixed ${userIsImpersonated ? "top-10" : "top-0"} z-overlay lg:z-auto right-0 ${mobileHelpers.showRightMenuDrawer ? "translate-x-0 flex" : "translate-x-full hidden"} ` +
            `lg:flex lg:relative lg:col-span-8 lg:top-auto lg:right-auto lg:transform-none ` +
            `flex-col p-6 w-64 ${userIsImpersonated ? "h-[calc(100vh-40px)]" : "h-dvh"} overflow-auto bg-gray-100 ` +
            `lg:flex-row lg:p-0 lg:w-auto lg:h-auto lg:overflow-visible lg:justify-end lg:bg-transparent`
          }
        >
          {/* {loggedInUserIsSysAdmin() !== true && ( */}
          {/* removed check because as of now we want to allow all features to be accessible via SYSADMIN_SUPERADMIN_USER_CAN_ACCESS_ALL_APP_FEATURES */}
          {true && (
            <>
              {/* Gear Icon - Admins Dropdown */}
              {!adminsMenuActive && <AdminsDropdown />}

              {/* Help, Chat, Warning, Notifications */}
              <div
                className={`${adminsMenuActive ? "" : "mt-6 "}lg:mt-0 lg:ml-6 flex items-center relative`}
              >
                <div className="w-full lg:w-auto flex flex-col lg:flex-row lg:items-center gap-6">
                  {
                    //getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_HELPMENU) >= ROLE_ACCESS_LEVELS.READ
                    true && (
                      <>
                        <div
                          className={`${
                            pathname.indexOf(routeUrls.dashboardChildren.help) >
                            -1
                              ? "bg-accent-blue-pale"
                              : "bg-white"
                          } rounded-full relative`}
                          onClick={handleHelpIconClick}
                        >
                          <IconWithCounter
                            src={svgIcons.HelpIcon}
                            alt="help-icon"
                            title={tTop("help")}
                          />
                          <MenuItemLabel label={tTop("help")} />
                        </div>

                        {openHelpPopup && (
                          <HelpPopup onClose={() => setOpenHelpPopup(false)} />
                        )}
                      </>
                    )
                  }
                  {
                    //getUserACLAccessLevel(ACL_NAMES.ACL_PROFILE_MESSAGING) >= ROLE_ACCESS_LEVELS.READ
                    true && (
                      <Link
                        to={routeUrls.dashboardChildren.chat}
                        onClick={hideMobileMenu}
                      >
                        <div
                          className={`${pathname.indexOf(routeUrls.dashboardChildren.chat) > -1 ? "bg-accent-blue-pale" : "bg-white"} rounded-full relative`}
                          onClick={() =>
                            navigate(routeUrls.dashboardChildren.chat)
                          }
                        >
                          <IconWithCounter
                            src={svgIcons.ChatIcon}
                            alt="chat-icon"
                            counter={allMessagingUnreadCount}
                            title={tTop("messaging")}
                          />
                          {/* value "chat-icon" being used in the condition */}
                          <MenuItemLabel label={tTop("messaging")} />
                        </div>
                      </Link>
                    )
                  }
                  <button
                    type="button"
                    className={`bg-white rounded-full relative ${modalsState.showAlertNotifications ? "z-modal" : ""}`}
                    onClick={() =>
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showAlertNotifications:
                            !modalsState.showAlertNotifications,
                        })
                      )
                    }
                  >
                    <IconWithCounter
                      src={svgIcons.WarningIcon}
                      alt="warning-icon"
                      counter={dataNotificationSummary?.total_alerts || 0}
                      title={tTop("alerts")}
                    />
                    <MenuItemLabel label={tTop("alerts")} />
                  </button>
                  <button
                    type="button"
                    className={`bg-white rounded-full relative ${modalsState.showNotifications ? "z-modal" : ""}`}
                    onClick={() =>
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showNotifications: !modalsState.showNotifications,
                        })
                      )
                    }
                  >
                    <IconWithCounter
                      src={svgIcons.NotificationIcon}
                      alt="notification-icon"
                      counter={
                        dataNotificationSummary?.total_notifications || 0
                      }
                      title={tTop("notifications")}
                    />
                    <MenuItemLabel label={tTop("notifications")} />
                  </button>
                </div>
                <ModalNotificationList />
                <ModalNotificationList alertsMode={true} />
              </div>
            </>
          )}

          <div className="lg:hidden flex flex-grow min-h-6" />

          {/* Profile Icon */}
          <div
            className={`flex flex-col-reverse lg:flex-row items-center relative select-none${showProfileDropdown ? " z-modal" : ""}`}
          >
            {/* Button */}
            <button
              type="button"
              className="hidden lg:flex items-center justify-center mt-6 lg:mt-0 lg:ml-5 rounded-full cursor-pointer relative peer"
              onClick={handleClickProfileIcon}
            >
              <AppAvatar
                bgColorClass={showProfileDropdown ? "bg-white" : "bg-gray-200"}
                initials={avatarInitials}
              />
            </button>

            {/* User Info Tooltip */}
            <div
              className={
                `lg:hidden${showProfileDropdown ? "" : " peer-hover:flex"} flex-col lg:absolute lg:top-full lg:right-0 lg:mt-2 ` +
                `lg:bg-white lg:border border-black/10 lg:shadow-lg lg:rounded-lg lg:px-4 lg:py-2 w-full lg:w-72 z-below-overlay ` +
                `mt-6 lg:mt-0`
              }
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-heading-gray444 leading-5">
                  {loggedInUser?.contactname || loggedInUser?.userid}
                </span>
                <span className="text-[10px] text-heading-gray888 leading-5">
                  {loggedInUser?.roleid}
                </span>
              </div>
              <span className="text-xs text-heading-gray888 mt-1 flex justify-between gap-4">
                <span>{loggedInUser?.contactemail}</span>
                {userTzIsApplicable &&
                  userTzForDisplay !== accountTzForDisplay && (
                    <span>{userTzForDisplay}</span>
                  )}
              </span>
              <span className="text-xs text-heading-gray888 font-bold flex justify-between gap-1 border-t border-black/10 pt-2 pb-1 mt-3">
                <span>
                  <span className="uppercase">{tTop("user_account")}:</span>
                  {loggedInUser?.accountid}
                </span>
                <span
                  className={`font-normal${userTzIsApplicable && userTzForDisplay !== accountTzForDisplay ? " line-through opacity-70" : ""}`}
                >
                  {accountTzForDisplay}
                </span>
              </span>
            </div>

            {/* Dropdown */}
            <div
              className={`lg:absolute top-full end-0 z-10 lg:mt-2 w-full lg:w-56 py-1 lg:py-0 divide-y divide-gray-100 rounded-md lg:border border-gray-100 bg-white lg:shadow-lg lg:z-modal flex-col ${showProfileDropdown ? "lg:flex" : "lg:hidden"}`}
              role="menu"
            >
              <div className="flex flex-col lg:p-2">
                {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ACCOUNT) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  <button
                    type="button"
                    className="block w-full text-left rounded-lg px-4 py-2 text-sm text-gray-500 cursor-pointer lg:hover:bg-gray-200 hover:text-gray-700"
                    role="menuitem"
                    onClick={() => handleNavigateTo("account")}
                  >
                    {tTop("account")}
                  </button>
                )}

                {/* {getUserACLAccessLevel(ACL_NAMES.ACL_PROFILE_CHANGE_PASSWORD) >=
                  ROLE_ACCESS_LEVELS.READ && ( */}
                <button
                  type="button"
                  className="block w-full text-left rounded-lg px-4 py-2 text-sm text-gray-500 cursor-pointer lg:hover:bg-gray-200 hover:text-gray-700"
                  role="menuitem"
                  onClick={() => handleNavigateTo("change_password")}
                >
                  {tTop("change_password")}
                </button>
                {/* )} */}

                {/* Disabled after discussion with client */}
                {/* <button
                  type="button"
                  className="block text-left rounded-lg px-4 py-2 text-sm text-gray-500 cursor-pointer hover:bg-gray-200 hover:text-gray-700"
                  role="menuitem"
                  onClick={() => {
                    setShowProfileDropdown(false)
                    dispatch(setModalsData({ ...modalsState, showNotificationPrefs: true }))
                  }}
                >
                  {tTop("notification_prefs")}
                </button> */}

                {thisOrgAllowsToggleFT === APP_CONFIG.TOGGLE_FT_ALLOWED.YES && (
                  <button
                    type="button"
                    className="block text-left rounded-lg px-4 py-2 text-sm text-gray-500 cursor-pointer lg:hover:bg-gray-200 hover:text-gray-700"
                    role="menuitem"
                    onClick={navigateToFT1_0App}
                  >
                    <span className="flex gap-2">
                      {tTop("goto_ft_1_0")}
                      {isLoadingFleetTrackLink && (
                        <img
                          src={svgIcons.LoadingAnimation}
                          alt="loading-animation"
                          className="block size-5 ml-2"
                        />
                      )}
                    </span>
                  </button>
                )}
              </div>

              {/* Session timeout timer */}
              <SessionTimeoutTimer expiresAt={sessionExpiryFromToken} />

              {/* Logout button */}
              <div className="lg:p-2 pt-1 mt-1 lg:pt-0 lg:mt-0">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm disabled:text-field-info-text/50 enabled:text-field-error-dark enabled:cursor-pointer lg:enabled:hover:bg-field-error-dark/15"
                  role="menuitem"
                  onClick={handleLogout}
                  disabled={userIsImpersonated || isLoading}
                >
                  {tTop(userIsImpersonated ? "logout_impersonated" : "logout")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Disabled after discussion with client */}
      {/* <ModalNotificationPrefs /> */}
      {/* Buzzer Alert Popups */}
      <ModalBuzzerAlertSingle />
      <ModalBuzzerAlertList />
    </>
  );
};

export default DashboardHeader;
